import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CheckCircle2, Loader2, MessageSquareWarning, X, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/components/ui/cn';
import Button from '@/components/ui/Button';
import { useFeedbackGeven } from '../hooks/useMeldingFeedback';
import {
  MAX_FEEDBACK_FOTOS,
  feedbackOpties,
  feedbackVraag,
  valideerFeedback,
  voegFeedbackFotosToe,
  type Feedback,
} from '../lib/feedbackRegels';

export interface FeedbackMelding {
  id: string;
  omschrijving: string;
  status: string;
  beoordeling: string | null;
  uitleg: string | null;
  beoordeeld_op?: string | null;
}

/**
 * De feedbackvraag aan de melder: "Jouw melding 'X' is opgelost — werkt het
 * nu?" met Ja/Nee en een opmerkingsveld (verplicht bij nee). "Ja" verstuurt
 * meteen; "Nee" vraagt eerst de toelichting. Compact = binnen de meldknop.
 */
export default function MeldingFeedbackKaart({
  melding,
  compact = false,
  uitgelicht = false,
}: {
  melding: FeedbackMelding;
  compact?: boolean;
  uitgelicht?: boolean;
}) {
  const geven = useFeedbackGeven();
  const [keuze, setKeuze] = useState<Feedback | null>(null);
  const [opmerking, setOpmerking] = useState('');
  // Foto's bij het antwoord (melding Pieter 3e6d223c, besluit Tom: max 5).
  const [fotos, setFotos] = useState<File[]>([]);
  const fotoInputRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  const voorbeelden = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => () => voorbeelden.forEach((u) => URL.revokeObjectURL(u)), [voorbeelden]);

  const kiesFotos = (lijst: FileList | null) => {
    if (!lijst) return;
    const r = voegFeedbackFotosToe(fotos, Array.from(lijst));
    setFotos(r.lijst);
    if (r.geweigerd > 0)
      toast.error(`Maximaal ${MAX_FEEDBACK_FOTOS} foto's; ${r.geweigerd} niet toegevoegd.`);
    if (r.geenAfbeelding > 0) toast.error('Alleen afbeeldingen kunnen mee.');
    if (fotoInputRef.current) fotoInputRef.current.value = '';
  };

  useEffect(() => {
    if (uitgelicht) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [uitgelicht]);

  const vraag = feedbackVraag(melding.status);
  const opties = feedbackOpties(melding.status);
  if (!vraag || opties.length === 0) return null;
  const isOpgelost = melding.status === 'opgelost';

  const verstuur = async (feedback: Feedback, tekst: string) => {
    const fout = valideerFeedback(melding.status, feedback, tekst);
    if (fout) {
      toast.error(fout);
      return;
    }
    try {
      await geven.mutateAsync({ meldingId: melding.id, feedback, opmerking: tekst, fotos });
      const optie = opties.find((o) => o.waarde === feedback);
      toast.success(
        optie?.heropent
          ? 'Bedankt — de melding staat weer open en Tom is op de hoogte.'
          : 'Bedankt voor je terugmelding.',
      );
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const kies = (optie: (typeof opties)[number]) => {
    if (!optie.opmerkingVerplicht) {
      void verstuur(optie.waarde, '');
      return;
    }
    setKeuze(optie.waarde);
  };

  const titel =
    melding.omschrijving.length > 90
      ? `${melding.omschrijving.slice(0, 90)}…`
      : melding.omschrijving;
  const toelichting = (melding.uitleg ?? melding.beoordeling ?? '').trim();

  return (
    <div
      ref={ref}
      data-testid="melding-feedback-kaart"
      className={cn(
        'rounded-lg border transition-shadow',
        compact
          ? 'px-3 py-2 bg-brand/5 border-brand/20'
          : 'px-4 py-3 bg-surface border-theme-border',
        uitgelicht && 'ring-2 ring-brand/50 shadow-md',
      )}
    >
      <div className="flex items-start gap-2">
        {isOpgelost ? (
          <CheckCircle2
            className={cn('flex-shrink-0 text-success', compact ? 'w-4 h-4 mt-0.5' : 'w-5 h-5')}
          />
        ) : (
          <MessageSquareWarning
            className={cn(
              'flex-shrink-0 text-foreground-tertiary',
              compact ? 'w-4 h-4 mt-0.5' : 'w-5 h-5',
            )}
          />
        )}
        <div className="min-w-0 flex-1">
          {!compact && (
            <p className="text-sm text-foreground">
              Jouw melding <span className="font-medium">‘{titel}’</span> is{' '}
              {isOpgelost ? 'opgelost' : 'afgewezen'} —{' '}
              <span className="font-semibold">{vraag}</span>
            </p>
          )}
          {compact && <p className="text-xs font-semibold text-foreground">{vraag}</p>}
          {!compact && toelichting && (
            <p className="mt-1 text-xs text-foreground-secondary whitespace-pre-line line-clamp-4">
              {toelichting}
            </p>
          )}

          <div className={cn('flex flex-wrap items-center gap-2', compact ? 'mt-1.5' : 'mt-2')}>
            {opties.map((o) => {
              const actief = keuze === o.waarde;
              return (
                <button
                  key={o.waarde}
                  type="button"
                  onClick={() => kies(o)}
                  disabled={geven.isPending}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50',
                    o.heropent || o.opmerkingVerplicht
                      ? actief
                        ? 'bg-error/15 text-error ring-1 ring-error/40'
                        : 'border border-theme-border text-foreground-secondary hover:text-error hover:border-error/40'
                      : 'bg-success/10 text-success hover:bg-success/20',
                  )}
                >
                  {geven.isPending && geven.variables?.feedback === o.waarde ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : o.opmerkingVerplicht ? (
                    <XCircle className="w-3.5 h-3.5" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  )}
                  {o.label}
                </button>
              );
            })}
          </div>

          {keuze && (
            <div className="mt-2 space-y-2">
              <textarea
                value={opmerking}
                onChange={(e) => setOpmerking(e.target.value)}
                rows={2}
                autoFocus
                placeholder={
                  isOpgelost
                    ? 'Wat werkt er nog niet? (verplicht)'
                    : 'Wat begrijp je niet? (verplicht)'
                }
                className="w-full px-3 py-2 text-sm bg-surface-secondary/50 border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fotoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => kiesFotos(e.target.files)}
                    data-testid="feedback-fotos-input"
                  />
                  <button
                    type="button"
                    onClick={() => fotoInputRef.current?.click()}
                    disabled={geven.isPending || fotos.length >= MAX_FEEDBACK_FOTOS}
                    className="inline-flex items-center gap-1 rounded-lg border border-theme-border px-2.5 py-1.5 text-xs font-medium text-foreground-secondary hover:bg-surface-secondary disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Foto's toevoegen
                  </button>
                  <span className="text-[11px] text-foreground-tertiary">
                    {fotos.length}/{MAX_FEEDBACK_FOTOS} · laat zien wat er nog niet klopt
                  </span>
                </div>
                {fotos.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="feedback-fotos">
                    {fotos.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative">
                        <img
                          src={voorbeelden[i]}
                          alt={`Foto ${i + 1}`}
                          className="h-16 w-16 rounded-lg border border-theme-border object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setFotos((prev) => prev.filter((_, j) => j !== i))}
                          disabled={geven.isPending}
                          className="absolute -right-1.5 -top-1.5 rounded-full bg-surface border border-theme-border p-0.5 text-foreground-secondary shadow hover:text-error"
                          aria-label={`Foto ${i + 1} verwijderen`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setKeuze(null);
                    setFotos([]);
                  }}
                  disabled={geven.isPending}
                >
                  Annuleren
                </Button>
                <Button
                  size="sm"
                  onClick={() => void verstuur(keuze, opmerking)}
                  disabled={geven.isPending || opmerking.trim() === ''}
                >
                  {geven.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Versturen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
